import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { toast } from "@/lib/toast";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, Pin, MessageSquare, X, Loader2, Palette } from "lucide-react";
import { resolveForumTheme, buildForumPalette, buildForumBg } from "@/lib/forumThemes";
import { canModerateForum } from "@/lib/accountRole";
import { ForumAuthorBadges } from "@/components/forum/ForumAuthorBadges";
import { MentionTextarea } from "@/components/forum/MentionTextarea";
import { ForumMediaUploader } from "@/components/forum/ForumMediaUploader";
import ForumPreferences from "@/components/forum/ForumPreferences";
import { ShareLinkButton } from "@/components/ShareLinkButton";

/**
 * Community Forum — REAL member posting (threads + replies), DB-backed.
 * Colors come from the signed-in member's Forum Appearance preference; a theme/skin only changes
 * what THAT user sees. Categories are admin-managed; threads/replies key off the stable user_id.
 */

const FALLBACK_CATEGORIES = [
  { label: "General", slug: "general" },
  { label: "Gear Talk", slug: "gear" },
  { label: "Range Reports", slug: "range" },
  { label: "Trade Talk", slug: "trade" },
  { label: "Intros", slug: "intros" },
];

export function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24); if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

function StatusTag({ thread, categories, palette }) {
  if (thread.pinned) {
    return (
      <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[1.5px] px-2 py-1 border shrink-0"
        style={{ color: palette.goldText, background: palette.gold, borderColor: palette.gold }}
        data-testid={`forum-thread-tag-${thread.thread_id}`}>
        <Pin className="w-3 h-3" strokeWidth={2} />Pinned
      </span>
    );
  }
  const cat = categories.find((c) => c.slug === thread.category_slug);
  return (
    <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[1.5px] px-2 py-1 border shrink-0"
      style={{ color: palette.tagLimeText, background: palette.tagLimeBg, borderColor: palette.tagLimeBorder }}
      data-testid={`forum-thread-tag-${thread.thread_id}`}>
      {cat?.label || "Thread"}
    </span>
  );
}

// New-thread composer modal.
function ThreadComposer({ open, onClose, categories, activeCat, palette, onCreated }) {
  const [category, setCategory] = useState(activeCat);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [images, setImages] = useState([]);
  const [video, setVideo] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setCategory(activeCat); setTitle(""); setBody(""); setImages([]); setVideo(null); } }, [open, activeCat]);
  if (!open) return null;
  const submit = async () => {
    if (title.trim().length < 3) { toast.error("Give your thread a title (at least 3 characters)."); return; }
    if (!body.trim()) { toast.error("Write something in the body."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/forum/threads", { category_slug: category, title: title.trim(), body: body.trim(), images, video });
      toast.success("Thread posted.");
      onClose();
      onCreated(data.thread);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 sm:p-8 bg-black/70 overflow-y-auto" data-testid="forum-composer" onClick={onClose}>
      <div className="w-full max-w-lg border mt-6" style={{ background: palette.rowSurface, borderColor: palette.cardBorder }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: palette.cardBorder }}>
          <h2 className="font-heading uppercase font-black text-lg" style={{ color: palette.text }}>New Thread</h2>
          <button onClick={onClose} data-testid="forum-composer-close" style={{ color: palette.textMuted }}><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-wide block mb-1" style={{ color: palette.textMuted }}>Category</label>
            <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="forum-composer-category"
              className="w-full bg-black/40 border px-3 py-2 font-mono text-sm outline-none" style={{ color: palette.text, borderColor: palette.cardBorder }}>
              {categories.map((c) => <option key={c.slug} value={c.slug} className="bg-black text-white">{c.label}</option>)}
            </select>
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-wide block mb-1" style={{ color: palette.textMuted }}>Title</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={140} data-testid="forum-composer-title"
              placeholder="What's your thread about?" className="w-full bg-black/40 border px-3 py-2 text-sm outline-none" style={{ color: palette.text, borderColor: palette.cardBorder }} />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-wide block mb-1" style={{ color: palette.textMuted }}>Body</label>
            <MentionTextarea P={palette} value={body} onChange={setBody} rows={6} maxLength={8000} testid="forum-composer-body"
              placeholder="Share the details… tag members with @username" className="w-full bg-black/40 border px-3 py-2 text-sm resize-y outline-none" style={{ color: palette.text, borderColor: palette.cardBorder }} />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-wide block mb-1" style={{ color: palette.textMuted }}>
              Photos &amp; video <span style={{ opacity: 0.6 }}>(optional · up to 4 photos + one 60s clip)</span>
            </label>
            <ForumMediaUploader P={palette} images={images} setImages={setImages} video={video} setVideo={setVideo} />
          </div>
          <button onClick={submit} disabled={busy} data-testid="forum-composer-submit"
            className="w-full inline-flex items-center justify-center gap-2 font-mono uppercase text-xs font-bold tracking-wide px-4 py-2.5 bg-olive hover:bg-olive-bright text-white transition-colors disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" strokeWidth={2.5} />} Post Thread
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Forum() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const theme = useMemo(() => resolveForumTheme(user), [user]);
  const PALETTE = useMemo(() => buildForumPalette(theme), [theme]);
  const forumBg = useMemo(() => buildForumBg(theme), [theme]);
  const [categories, setCategories] = useState(FALLBACK_CATEGORIES);
  const [allCats, setAllCats] = useState(FALLBACK_CATEGORIES); // incl. retired — for resolving thread tags
  const [activeCat, setActiveCat] = useState(FALLBACK_CATEGORIES[0].slug);
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composerOpen, setComposerOpen] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/forum/categories", { params: { include_retired: 1 } }).then(({ data }) => {
      const all = data?.categories || [];
      if (!alive || all.length === 0) return;
      setAllCats(all.map((c) => ({ label: c.label, slug: c.slug, status: c.status })));
      const active = all.filter((c) => c.status !== "retired").map((c) => ({ label: c.label, slug: c.slug }));
      if (active.length === 0) return;
      setCategories(active);
      setActiveCat((cur) => (active.some((c) => c.slug === cur) ? cur : active[0].slug));
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const loadThreads = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/forum/threads", { params: { category: activeCat } });
      setThreads(data.threads || []);
    } catch { setThreads([]); }
    finally { setLoading(false); }
  }, [activeCat]);
  useEffect(() => { loadThreads(); }, [loadThreads]);

  // Posting lockdown (Phase 3): only staff/moderators start top-level threads. Everyone else
  // reads + replies. Mirrors the server gate in forum.create_thread (_is_forum_mod).
  const canStartThread = canModerateForum(user);

  const openComposer = () => {
    if (!user) { toast("Sign in to start a thread."); return; }
    if (!canStartThread) {
      toast("Only staff and moderators can start new threads. You can still reply to any thread.");
      return;
    }
    setComposerOpen(true);
  };

  const newThreadBtn = (testid) => {
    if (!canStartThread) return null;
    return (
      <button onClick={openComposer} data-testid={testid}
        className="inline-flex items-center gap-2 font-mono uppercase text-xs font-bold tracking-wide px-4 py-2.5 transition-opacity hover:opacity-90 shrink-0"
        style={{ background: PALETTE.accent, color: PALETTE.accentText }}>
        <Plus className="w-4 h-4" strokeWidth={2.5} /> New Thread
      </button>
    );
  };

  // Every logged-in member can theme their own forum view (per-user preference).
  const prefsBtn = () => {
    if (!user) return null;
    return (
      <button onClick={() => setPrefsOpen(true)} data-testid="forum-preferences-btn"
        className="inline-flex items-center gap-2 font-mono uppercase text-xs font-bold tracking-wide px-3.5 py-2.5 border transition-colors shrink-0 hover:bg-white/5"
        style={{ color: PALETTE.text, borderColor: PALETTE.cardBorder }}>
        <Palette className="w-4 h-4" strokeWidth={2} /> Preferences
      </button>
    );
  };

  return (
    <div className="min-h-screen" style={{ color: PALETTE.text }} data-testid="forum-page">
      <Navbar />
      <div className="relative">
        {/* Procedural camo/topo background (dark gradient + topo contour + multicam camo). For a CUSTOM
            accent the whole stack is hue-rotated; the accent-colored UI below uses the literal hex. */}
        <div aria-hidden data-testid="forum-bg" className="pointer-events-none absolute inset-0 z-0" style={forumBg} />
        <div className="relative z-[1] max-w-[900px] mx-auto px-4 sm:px-6 py-10 sm:py-14 min-h-[80vh]">
          {/* 1. Header */}
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h1 className="font-heading text-4xl sm:text-5xl font-black uppercase tracking-tight" style={{ color: "#ffffff" }} data-testid="forum-title">Forum</h1>
            <div className="flex items-center gap-2">
              <ShareLinkButton sharePath="forum" title="OpticSwap Forum" testid="forum-share-btn"
                className="inline-flex items-center gap-2 font-mono uppercase text-xs font-bold tracking-wide px-3.5 py-2.5 border transition-colors shrink-0 hover:bg-white/5"
                style={{ color: PALETTE.text, borderColor: PALETTE.cardBorder }} />
              {prefsBtn()}
              {newThreadBtn("forum-new-thread-btn")}
            </div>
          </div>

          {/* 2. Category chips */}
          <div className="mt-6 flex flex-wrap gap-2" data-testid="forum-categories">
            {categories.map((c) => {
              const active = c.slug === activeCat;
              return (
                <button key={c.slug} onClick={() => setActiveCat(c.slug)} data-testid={`forum-chip-${c.slug}`} aria-pressed={active}
                  className="font-mono uppercase text-[11px] tracking-[1px] px-3.5 py-2 border transition-colors"
                  style={active
                    ? { background: PALETTE.chipActiveBg, color: PALETTE.chipActiveText, borderColor: PALETTE.chipActiveBg }
                    : { background: PALETTE.chipBg, color: PALETTE.chipText, borderColor: PALETTE.chipBorder }}>
                  {c.label}
                </button>
              );
            })}
          </div>

          {/* 3. Thread list */}
          <div className="mt-8 flex flex-col gap-2" data-testid="forum-thread-list">
            {loading ? (
              <div className="px-5 py-12 text-center" style={{ color: PALETTE.textMuted }} data-testid="forum-loading">
                <Loader2 className="w-5 h-5 animate-spin mx-auto" />
              </div>
            ) : threads.length === 0 ? (
              <div className="px-5 py-14 text-center border" style={{ borderColor: PALETTE.cardBorder, background: PALETTE.rowSurface }} data-testid="forum-empty">
                <MessageSquare className="w-8 h-8 mx-auto mb-3" style={{ color: PALETTE.textMuted }} strokeWidth={1.4} />
                <p className="font-heading uppercase font-black text-xl" style={{ color: PALETTE.text }}>No threads yet</p>
                <p className="mt-1 font-mono text-xs" style={{ color: PALETTE.textMuted }} data-testid="forum-empty-note">
                  {canStartThread ? "Be the first to start the conversation in this category." : "No threads here yet — new topics are posted by the OpticSwap team."}
                </p>
                {canStartThread && <div className="mt-4 flex justify-center">{newThreadBtn("forum-empty-new-thread")}</div>}
              </div>
            ) : (
              threads.map((t) => (
                <button key={t.thread_id} onClick={() => navigate(`/forum/thread/${t.thread_id}`)} data-testid={`forum-thread-${t.thread_id}`}
                  className="w-full text-left flex items-start gap-4 px-4 sm:px-5 py-4 border transition-opacity hover:opacity-90"
                  style={{ borderColor: PALETTE.cardBorder, background: PALETTE.rowSurface }}>
                  <div className="pt-0.5"><StatusTag thread={t} categories={allCats} palette={PALETTE} /></div>
                  <div className="min-w-0 flex-1">
                    <h2 className="font-body font-bold text-base sm:text-lg leading-snug line-clamp-2" style={{ color: PALETTE.text }} data-testid={`forum-thread-title-${t.thread_id}`}>{t.title}</h2>
                    <p className="mt-1.5 font-mono text-[11px] tracking-[0.3px] flex flex-wrap items-center gap-x-1.5 gap-y-1" style={{ color: PALETTE.textMuted }} data-testid={`forum-thread-meta-${t.thread_id}`}>
                      <span>@{t.author_username}</span>
                      <ForumAuthorBadges role={t.author_role} badges={t.author_badges} accountType={t.author_account_type} />
                      <span>· {timeAgo(t.last_activity_at)}</span>
                    </p>
                  </div>
                  <div className="text-right shrink-0 pl-1">
                    <div className="font-heading font-black text-2xl sm:text-3xl tabular-nums leading-none" style={{ color: PALETTE.lime }} data-testid={`forum-thread-replies-${t.thread_id}`}>{t.reply_count}</div>
                    <div className="font-mono text-[9px] uppercase tracking-[1.5px] mt-1" style={{ color: PALETTE.textMuted }}>{t.reply_count === 1 ? "reply" : "replies"}</div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      </div>
      <ThreadComposer open={composerOpen} onClose={() => setComposerOpen(false)} categories={categories}
        activeCat={activeCat} palette={PALETTE} onCreated={(th) => navigate(`/forum/thread/${th.thread_id}`)} />
      <ForumPreferences open={prefsOpen} onClose={() => setPrefsOpen(false)} me={user} onSaved={refresh} />
    </div>
  );
}
